import { forwardRef, Ref, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { Menu as MantineMenu } from '@mantine/core'
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react'
import clsx from 'clsx'
import { useDispatch, useSelector } from 'common/hooks'
import { IMenuItem } from 'common/models'
import { hasProperty } from 'common/utils'
import { closeMoreMenu, openMoreMenu } from 'iframe/store'

interface IProps {
  visibleMenu: IMenuItem[]
  hiddenMenu: IMenuItem[]
}

function Menu({ visibleMenu, hiddenMenu }: IProps, ref: Ref<HTMLDivElement>) {
  const { t } = useTranslation()
  const location = useLocation()
  const dispatch = useDispatch()
  const { isMoreMenuOpen } = useSelector((state) => state.iframe)

  const [, setChildMenuStates] = useState<Record<string, boolean>>({})
  const [expandedHiddenMenuItem, setExpandedHiddenMenuItem] = useState<string | null>(
    null,
  )

  // Funkcja pomocnicza do sprawdzania czy link jest aktywny
  const isLinkActive = useCallback(
    (link: string): boolean => {
      const currentPath = location.pathname.replace(/\?.*$/, '')
      const normalizedLink = link.replace(/\?.*$/, '')

      // Specjalny przypadek dla root path '/'
      if (normalizedLink === '/') {
        return currentPath === '/'
      }

      // Dokładne dopasowanie
      return currentPath === normalizedLink
    },
    [location.pathname],
  )

  // Funkcja pomocnicza do sprawdzania czy sublink jest aktywny
  const isSubLinkActive = useCallback(
    (parentLink: string, subLink: string): boolean => {
      const currentPath = location.pathname.replace(/\?.*$/, '')
      const fullSubLinkPath = `${parentLink}/${subLink}`.replace(/\/+/g, '/') // Usuwa podwójne slashe

      // Sprawdź dokładne dopasowanie lub czy pathname zaczyna się od pełnej ścieżki sublinka
      return (
        currentPath === fullSubLinkPath || currentPath.startsWith(`${fullSubLinkPath}/`)
      )
    },
    [location.pathname],
  )

  // Funkcja pomocnicza do sprawdzania czy którykolwiek sublink jest aktywny
  const hasActiveSubLink = useCallback(
    (parentLink: string, childs?: IMenuItem[]): boolean => {
      if (!childs || childs.length === 0) return false
      return childs.some((sub) => isSubLinkActive(parentLink, sub.link))
    },
    [isSubLinkActive],
  )

  const handleMoreMenuChange = (opened: boolean) => {
    if (opened) {
      dispatch(openMoreMenu())
    } else {
      dispatch(closeMoreMenu())
      // Reset accordion gdy menu się zamyka
      setExpandedHiddenMenuItem(null)
    }
  }

  // Close submenu when main menu is closed
  useEffect(() => {
    if (!isMoreMenuOpen) {
      setExpandedHiddenMenuItem(null)
    }
  }, [isMoreMenuOpen])

  return (
    <div className={'flex items-center gap-6 w-full min-w-0'} ref={ref}>
      <nav className={'flex items-center gap-6 min-w-0 overflow-hidden'}>
        {visibleMenu.map((item) => {
          return hasProperty(item, 'childs') ? (
            <MantineMenu
              classNames={{ dropdown: 'flex flex-col gap-1 !w-[250px] p-2' }}
              closeDelay={400}
              key={item.link}
              offset={10}
              openDelay={100}
              position={'bottom-start'}
              trigger={'hover'}
              withinPortal={false}
            >
              <MantineMenu.Target>
                <button
                  className={clsx(
                    'no-underline font-normal text-md text-white bg-transparent p-0 border-0 hover:cursor-pointer whitespace-nowrap flex-shrink-0',
                    location.pathname === item.link ||
                      (item.childs?.find((i) => location.pathname.includes(i.link)) &&
                        '!underline !underline-offset-4 decoration-2'),
                  )}
                >
                  {t(item.label)}
                </button>
              </MantineMenu.Target>
              <MantineMenu.Dropdown>
                {(item.childs ?? []).map((i) => (
                  <Link
                    className={clsx(
                      'w-full no-underline font-medium text-black hover:bg-gray-200 p-2 rounded-sm',
                      location.pathname.replace('/', '').includes(i.link) &&
                        '!underline !underline-offset-4 decoration-2',
                    )}
                    key={`${item.link}/${i.link}`}
                    onClick={() =>
                      setChildMenuStates((prev) => ({ ...prev, [item.link]: false }))
                    }
                    to={`${item.link}/${i.link}`}
                  >
                    {t(i.label)}
                  </Link>
                ))}
              </MantineMenu.Dropdown>
            </MantineMenu>
          ) : (
            <Link
              className={clsx(
                'no-underline font-normal text-md text-white whitespace-nowrap flex-shrink-0',
                location.pathname === item.link.replace(/\?.*$/, '') &&
                  '!underline !underline-offset-4 decoration-2',
              )}
              key={item.link}
              to={item.link}
            >
              {t(item.label)}
            </Link>
          )
        })}
      </nav>
      {hiddenMenu.length > 0 && (
        <div className={'flex items-center flex-shrink-0'}>
          <MantineMenu
            classNames={{ dropdown: 'flex flex-col gap-1 !w-[250px] p-2' }}
            onChange={handleMoreMenuChange}
            opened={isMoreMenuOpen}
            withinPortal={false}
          >
            <MantineMenu.Target>
              <button
                className={
                  'no-underline font-normal text-md text-white bg-transparent p-0 border-0 hover:cursor-pointer whitespace-nowrap flex-shrink-0 flex items-center gap-1'
                }
              >
                <span>{t('nav.more')}</span>
                {isMoreMenuOpen ? (
                  <IconChevronUp size={16} stroke={2} />
                ) : (
                  <IconChevronDown size={16} stroke={2} />
                )}
              </button>
            </MantineMenu.Target>
            <MantineMenu.Dropdown>
              {hiddenMenu.map((item, index) => {
                if (hasProperty(item, 'childs')) {
                  const isExpanded = expandedHiddenMenuItem === item.link
                  const isSubLinkActiveForParent = hasActiveSubLink(
                    item.link,
                    item.childs,
                  )

                  return (
                    <div className={index > 0 ? 'mt-1' : ''} key={item.link}>
                      <button
                        className={clsx(
                          'bg-transparent cursor-pointer border-0 w-full flex items-center justify-between font-medium text-base text-black hover:bg-gray-100 px-2 py-1.5 rounded transition-colors duration-150',
                          isExpanded && 'bg-gray-50',
                          isSubLinkActiveForParent &&
                            '!underline !underline-offset-4 decoration-2',
                        )}
                        onClick={() => {
                          // Accordion behavior - tylko jeden element otwarty naraz
                          setExpandedHiddenMenuItem(isExpanded ? null : item.link)
                        }}
                      >
                        <span className={'flex-1 text-left'}>{t(item.label)}</span>
                        <div className={'ml-2 flex-shrink-0 text-gray-500'}>
                          {isExpanded ? (
                            <IconChevronUp size={16} stroke={2} />
                          ) : (
                            <IconChevronDown size={16} stroke={2} />
                          )}
                        </div>
                      </button>
                      {isExpanded && (
                        <div
                          className={'flex flex-col border-l-2 border-gray-200 ml-3 my-1'}
                        >
                          {item.childs?.map((sub) => {
                            const isSubActive = isSubLinkActive(item.link, sub.link)
                            return (
                              <Link
                                className={clsx(
                                  'no-underline font-normal text-base text-gray-700 whitespace-nowrap hover:bg-gray-100 px-2 py-1 rounded transition-colors duration-150',
                                  isSubActive &&
                                    '!text-black !font-medium bg-gray-50 !underline !underline-offset-4 decoration-2',
                                )}
                                key={`${item.link}-${sub.link}-${sub.label}`}
                                onClick={() => {
                                  setExpandedHiddenMenuItem(null)
                                  dispatch(closeMoreMenu())
                                }}
                                to={`${item.link}/${sub.link}`}
                              >
                                {t(sub.label)}
                              </Link>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                }

                const isActive = isLinkActive(item.link)

                return (
                  <Link
                    className={clsx(
                      'block no-underline font-medium text-base text-black hover:bg-gray-100 px-2 py-1.5 rounded transition-colors duration-150',
                      isActive && '!underline !underline-offset-4 decoration-2',
                      index > 0 && 'mt-1',
                    )}
                    key={item.link}
                    onClick={() => dispatch(closeMoreMenu())}
                    to={item.link}
                  >
                    {t(item.label)}
                  </Link>
                )
              })}
            </MantineMenu.Dropdown>
          </MantineMenu>
        </div>
      )}
    </div>
  )
}

export default forwardRef<HTMLDivElement, IProps>(Menu)
